import { orderBy } from 'lodash';
import { GameDayModel } from '../../../models/gameday-model';
import { amountFormat, dateFormat } from '../../../gameday/gameday.constants';
import {
  OrderSummaryData,
  OrderSummary,
  ViewOrderSummary
} from '../../models/summary';

export function orderSummaryMapper({
  orders,
  activeAccount
}: {
  activeAccount: string;
  orders: GameDayModel.Order[];
}): { data: OrderSummaryData; activeAccount: string } {
  const result: OrderSummaryData = {};
  const filteredOrders = orders.filter(
    (order) => !!order.firmId && !!order.accountName
  );
  const ordersReducedByAccount = reduceByAccount(filteredOrders);

  Object.keys(ordersReducedByAccount).forEach((key) => {
    const ordersCombinedByMaturity = reduceByMaturityId(
      ordersReducedByAccount[key]
    );
    result[key] = Object.keys(ordersCombinedByMaturity).map(
      (id) => ordersCombinedByMaturity[id]
    );
  });

  return {
    data: result,
    activeAccount
  };
}

function reduceByAccount(
  target: GameDayModel.Order[]
): { [key: string]: GameDayModel.Order[] } {
  return target.reduce((accumulator, next: GameDayModel.Order) => {
    if (!Object.prototype.hasOwnProperty.call(accumulator, next.accountName)) {
      accumulator[next.accountName] = [next];
    } else {
      accumulator[next.accountName].push(next);
    }
    return accumulator;
  }, {});
}

function reduceByMaturityId(
  target: GameDayModel.Order[]
): { [key: number]: OrderSummary } {
  return target.reduce((accumulator, next: GameDayModel.Order) => {
    if (!Object.prototype.hasOwnProperty.call(accumulator, next.maturity.id)) {
      accumulator[next.maturity.id] = {
        name: getName(next),
        amount: next.amount / 1000,
        id: next.maturity.id,
        code: next.maturity.series.code,
        date: next.maturity.maturityDate,
        coupon: next.maturity.coupon
      };
    } else {
      accumulator[next.maturity.id].amount += next.amount / 1000;
    }
    return accumulator;
  }, {});
}

function getName(order: GameDayModel.Order): string {
  const code = order.maturity.series.code;
  const date = order.maturity.maturityDate.format(dateFormat);
  const coupon = order.maturity.coupon;
  return !!coupon || coupon === 0
    ? `${code}: ${date}, ${coupon.toFixed(3)}%`
    : `${code}: ${date}`;
}

export function viewOrderSummaryMapper({
  data,
  activeAccount
}: {
  data: OrderSummaryData;
  activeAccount: string;
}) {
  const orders: ViewOrderSummary[] = data[activeAccount].map(
    (order: OrderSummary) => ({
      ...order,
      amountFormatted: amountFormat.format(order.amount)
    })
  );

  return sortByNameAsc(orders);
}

function sortByNameAsc(orders: ViewOrderSummary[]): ViewOrderSummary[] {
  return orderBy(orders, ['code', 'date', 'coupon'], ['asc', 'asc', 'asc']);
}
